/**
 * Loads Google Identity Services (GIS) and resolves with the `window.google`
 * namespace once `google.accounts.id` is ready to use.
 *
 * Idempotent — repeated calls return the same cached promise.
 */
let loadPromise = null;

export const loadGoogleScript = () => {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('No window'));
      return;
    }
    if (window.google?.accounts?.id) {
      resolve(window.google);
      return;
    }

    const existing = document.getElementById('google-identity-script');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * The Google OAuth client id used to identify this web app to Google.
 * Must be configured in Google Cloud Console as a "Web application" OAuth
 * client, with this site's origin listed under "Authorized JavaScript origins."
 *
 * Set via the env var REACT_APP_GOOGLE_CLIENT_ID at build time.
 */
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
